import React, { useEffect, useRef, useState } from 'react';
import Markdown from '../Markdown';
import CommentList from './CommentList';
import LinkedWikis from './LinkedWikis';
import LinkedWorklogs from './LinkedWorklogs';
import EntryList from './EntryList';
import LinkTaskPicker from './LinkTaskPicker';
import StepList from './StepList';
import formatDuration from '../../utils/formatDuration';
import type { Project } from '../../types/project';
import type { TimeEntry } from '../../types/timeEntry';
import api from '../../lib/api';
import useMarkdownImages from '../../hooks/useMarkdownImages';
import type { TodoTask } from '../../types/todo';
import type { Priority } from '../../types/plan';

type DetailPaneProps = {
  task: TodoTask;
  projects: Project[];
  categories: string[];
  today: string;
  // 在右栏点「加入今天」撞上限时，解释不能只出现在中栏 —— 操作的地方和
  // 得到反馈的地方得是同一处。这里不重复中栏那段长文案，让按钮自己说话。
  capBlocked: boolean;
  error: string | null;
  runningEntry: TimeEntry | null;
  onClose: () => void;
  onToggle: (task: TodoTask) => void;
  onPatch: (
    id: number,
    patch: Record<string, unknown>,
    opts?: { capAware?: boolean }
  ) => Promise<boolean>;
  // 加星 / 切紧急走乐观更新：右栏这两个开关和中栏的星标是同一类动作，
  // 不该一个立刻响应、另一个等往返。
  onOptimistic: (task: TodoTask, patch: Partial<TodoTask>) => void;
  onDestroy: (id: number) => void;
  onRefresh: () => void;
  onCreateStep: (parentId: number, title: string) => void;
  onToggleStep: (stepId: number, completed: boolean) => void;
  onRenameStep: (stepId: number, title: string) => void;
  onDeleteStep: (stepId: number) => void;
  // 把一条已有任务挂到当前任务下面。写的是**那一条**的 parent_id，不是当前
  // 任务的 —— 两个入口写同一列，方向相反。
  onLinkExisting: (childId: number) => void;
  // 解绑：把当前任务自己的 parent_id 置 null。
  onUnlinkParent: () => void;
  onStartTimer: (taskId: number) => void;
  onStopTimer: () => void;
  // 「扔给 AI」。浮层由调用方挂载而不是这里 —— DetailPane 一直是全受控的
  // 展示组件，全部动作都由 props 传进来。TaskPage 复用它，独立页因此同时
  // 就有了这个入口。
  onHandoff: (task: TodoTask) => void;
};

const ACT =
  'flex w-full items-center gap-[10px] border-0 border-b border-todo-line px-[13px] py-[10px] text-left text-[13px] last:border-b-0 hover:bg-todo-card2';
const CARD = 'mb-[9px] rounded-[9px] border border-todo-line bg-todo-card shadow-sm';
const ICO = 'w-[15px] flex-none text-center';

// 过期了几天。没填截止日、或还没到期，一律回 null —— 没过期时右边那个
// 日期输入框本身已经把话说完了，不需要再加一句。
function overdueDays(dueOn: string | null, today: string): number | null {
  if (!dueOn) return null;

  const days = Math.round(
    (new Date(`${today}T00:00:00`).getTime() - new Date(`${dueOn}T00:00:00`).getTime()) / 86_400_000
  );

  return days > 0 ? days : null;
}

const DetailPane: React.FC<DetailPaneProps> = ({
  task,
  projects,
  categories,
  today,
  capBlocked,
  error,
  runningEntry,
  onClose,
  onToggle,
  onPatch,
  onOptimistic,
  onDestroy,
  onRefresh,
  onCreateStep,
  onToggleStep,
  onRenameStep,
  onDeleteStep,
  onLinkExisting,
  onUnlinkParent,
  onStartTimer,
  onStopTimer,
  onHandoff,
}) => {
  const [title, setTitle] = useState(task.title);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [note, setNote] = useState(task.description ?? '');
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const [estimate, setEstimate] = useState(task.estimated_minutes ? String(task.estimated_minutes) : '');

  // 备注是「点击编辑」：常态渲染 markdown，点一下才换成源码框。
  const [editingNote, setEditingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const noteCardRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const {
    takeFiles: takeNoteFiles,
    uploading: noteUploading,
    handlers: noteImageHandlers,
  } = useMarkdownImages({
    upload: (file: File) => api.taskImages.create(task.id, file),
    setValue: setNote,
    textareaRef: noteRef,
    onError: setNoteError,
  });

  const commitNote = () => {
    if (note !== (task.description ?? '')) onPatch(task.id, { description: note });
    setEditingNote(false);
  };

  // 换任务、或局部重载后服务端值变了，输入框跟着重置
  useEffect(() => {
    setTitle(task.title);
    setNote(task.description ?? '');
    setEstimate(task.estimated_minutes ? String(task.estimated_minutes) : '');
  }, [task.id, task.title, task.description, task.estimated_minutes]);

  // 换任务时把删除确认收回去，免得在 A 上点了删除、切到 B 时按钮还是「确认删除？」
  useEffect(() => {
    setConfirmingDelete(false);
    setEditingNote(false);
    setNoteError(null);
  }, [task.id]);

  // 标题是 textarea（长标题要换行），高度跟着内容走
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);

  const commitTitle = () => {
    const next = title.trim();
    // 空标题存不进去（模型 validates :title, presence: true）。与其让它报错，
    // 不如直接回滚——想删除有底部那个按钮。
    if (!next) {
      setTitle(task.title);
      return;
    }
    if (next !== task.title) onPatch(task.id, { title: next });
  };

  const inToday = task.planned_for === today;
  const overdue = overdueDays(task.due_on, today);
  const running = runningEntry?.task_id === task.id;

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden border-l border-todo-line bg-todo-side">
      <div className="min-h-0 flex-1 overflow-y-auto p-[14px]">
        {error && (
          <div className="mb-[9px] rounded-lg border border-todo-warnLine bg-todo-warn px-3 py-2 text-[12px] text-todo-urgent">
            {error}
          </div>
        )}

        <div className={CARD}>
          <div className="flex items-start gap-[10px] p-[13px]">
            <input
              type="checkbox"
              className="todo-check"
              checked={task.completed}
              onChange={() => onToggle(task)}
              aria-label="完成"
            />
            <textarea
              ref={titleRef}
              value={title}
              rows={1}
              onChange={(event) => setTitle(event.target.value)}
              onBlur={commitTitle}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  titleRef.current?.blur();
                }
                if (event.key === 'Escape') {
                  setTitle(task.title);
                  titleRef.current?.blur();
                }
              }}
              aria-label="任务标题"
              className={`flex-1 resize-none overflow-hidden border-0 bg-transparent text-[15px] font-medium leading-snug outline-none ${
                task.completed ? 'text-todo-muted line-through' : 'text-todo-ink'
              }`}
            />
            <button
              type="button"
              aria-label={task.important ? '取消重要' : '标为重要'}
              onClick={() => onOptimistic(task, { important: !task.important })}
              className={`text-[14px] ${task.important ? 'text-todo-star' : 'text-todo-line2'}`}
            >
              {task.important ? '★' : '☆'}
            </button>
            {/* 独立页入口。不做「复制链接」按钮——一个 <a> 就够，浏览器自带
                复制地址的能力。 */}
            <a
              href={`/todo/${task.url_key}`}
              title="在独立页面打开"
              aria-label="在独立页面打开"
              className="px-0.5 text-[13px] leading-[19px] text-todo-muted hover:text-todo-accent"
            >
              ⧉
            </a>
            <button
              type="button"
              aria-label="关闭详情"
              onClick={onClose}
              className="px-0.5 text-[15px] leading-[19px] text-todo-muted hover:text-todo-ink"
            >
              ✕
            </button>
          </div>

          {/* 详情栏里唯一一个会把你带出这个页面的动作，所以是主按钮。 */}
          <div className="flex gap-1.5 border-t border-todo-line px-[13px] py-2.5">
            <button
              type="button"
              onClick={() => onHandoff(task)}
              className="flex-1 rounded-md bg-todo-accent px-3 py-1.5 text-[12.5px] font-medium text-white"
            >
              ✦ 扔给 AI
            </button>
          </div>

          {/* 有父任务时先说清「我是谁的子任务」，再列自己的步骤。 */}
          {task.parent && (
            <div className="flex items-center gap-[9px] border-t border-todo-line px-[13px] py-2 text-[13px]">
              <span className="flex-none text-todo-muted">父任务</span>
              <a
                href={`/todo/${task.parent.url_key}`}
                className="min-w-0 flex-1 truncate text-todo-ink2 hover:text-todo-accent"
              >
                {task.parent.title}
              </a>
              <button
                type="button"
                title="从父任务解绑"
                aria-label="从父任务解绑"
                onClick={onUnlinkParent}
                className="flex-none px-1 text-[12px] text-todo-line2 hover:text-todo-urgent"
              >
                ✕
              </button>
            </div>
          )}

          <StepList
            steps={task.subtasks}
            onToggleStep={onToggleStep}
            onRenameStep={onRenameStep}
            onDeleteStep={onDeleteStep}
            onCreateStep={(stepTitle) => onCreateStep(task.id, stepTitle)}
          />

          {/* 子任务不显示这个入口 —— 给子任务挂子任务就是第三层，
              Task#parent_must_be_a_root 会拒，那个入口点了只会报错。 */}
          {!task.parent_id && <LinkTaskPicker parentId={task.id} onPick={onLinkExisting} />}
        </div>

        <div className={CARD}>
          {/* 「加入今天」是唯一会撞上限的动作，所以只有它带 capAware */}
          <button
            type="button"
            className={`${ACT} ${inToday ? 'text-todo-accent' : 'text-todo-ink2'}`}
            onClick={() => onPatch(task.id, { planned_for: inToday ? null : today }, { capAware: !inToday })}
          >
            <span
              className={`${ICO} ${
                capBlocked && !inToday
                  ? 'text-todo-urgent'
                  : inToday
                    ? 'text-todo-accent'
                    : 'text-todo-muted'
              }`}
            >
              ☀
            </span>
            <span className="flex-1">{inToday ? '已加入「今天」' : '加入「今天」'}</span>
            {inToday && <span className="text-[12px] text-todo-accent">点此移出</span>}
            {capBlocked && !inToday && (
              <span className="text-[12px] text-todo-urgent">今天已满 5 条</span>
            )}
          </button>

          {/* 「加入今天」只能表达今天做还是不做。被 5 条上限挤出去的事需要一个
              去处，而「待办池」没有时间概念——这一行是给「周四做」用的。
              选到今天时同样受上限约束，所以带 capAware。 */}
          <label className={`${ACT} text-todo-ink2`}>
            <span className={`${ICO} text-todo-muted`}>▤</span>
            <span className="flex-1">计划日期</span>
            <input
              type="date"
              value={task.planned_for ?? ''}
              onChange={(event) => {
                const next = event.target.value === '' ? null : event.target.value;
                if (next === (task.planned_for ?? null)) return;
                onPatch(task.id, { planned_for: next }, { capAware: next === today });
              }}
              className="border-0 bg-transparent text-right text-[12px] tabular-nums text-todo-muted outline-none"
            />
          </label>

          {/* 截止日。与「计划日期」并列而不是二选一：那一行是「我打算哪天做」，
              这一行是「最晚哪天要做完」。
              不带 capAware —— 它不占「今天」的额度；过期也不会被退回待办池。
              逾期那句用 muted 而不是 todo-urgent：留痕但不催，排红就是催。 */}
          <label className={`${ACT} text-todo-ink2`}>
            <span className={`${ICO} text-todo-muted`}>⧖</span>
            <span className="flex-1">截止日</span>
            {overdue !== null && (
              <span className="mr-2 text-[12px] text-todo-muted">逾期 {overdue} 天</span>
            )}
            <input
              type="date"
              value={task.due_on ?? ''}
              onChange={(event) => {
                const next = event.target.value === '' ? null : event.target.value;
                if (next === (task.due_on ?? null)) return;
                onPatch(task.id, { due_on: next });
              }}
              className="border-0 bg-transparent text-right text-[12px] tabular-nums text-todo-muted outline-none"
            />
          </label>

          {/* 优先级。形状照抄下面「精力类型」那一行——同样是可空的多值枚举，
              首项「未标」就是 nil。
              「未标」与 p4 不是一回事：前者是没判过，后者是判过、就是最低。
              这个区分正是这个字段存在的理由（PR #44）。 */}
          <label className={`${ACT} text-todo-ink2`}>
            <span className={`${ICO} text-todo-muted`}>◆</span>
            <span className="flex-1">优先级</span>
            <select
              value={task.priority ?? ''}
              onChange={(event) =>
                onPatch(task.id, {
                  priority: event.target.value === '' ? null : (event.target.value as Priority),
                })
              }
              className="border-0 bg-transparent text-right text-[12px] text-todo-muted outline-none"
            >
              <option value="">未标</option>
              <option value="p1">p1 最高</option>
              <option value="p2">p2</option>
              <option value="p3">p3</option>
              <option value="p4">p4 最低</option>
            </select>
          </label>

          <button
            type="button"
            className={`${ACT} ${task.important ? 'text-todo-accent' : 'text-todo-ink2'}`}
            onClick={() => onOptimistic(task, { important: !task.important })}
          >
            <span className={`${ICO} ${task.important ? 'text-todo-accent' : 'text-todo-muted'}`}>★</span>
            <span className="flex-1">重要</span>
          </button>

          <label className={`${ACT} text-todo-ink2`}>
            <span className={`${ICO} text-todo-muted`}>◷</span>
            <span className="flex-1">预估时长</span>
            <input
              value={estimate}
              onChange={(event) => setEstimate(event.target.value.replace(/\D/g, ''))}
              onBlur={() => {
                const next = estimate === '' ? null : Number(estimate);
                if (next !== task.estimated_minutes) onPatch(task.id, { estimated_minutes: next });
              }}
              placeholder="未设置"
              className="w-16 border-0 bg-transparent text-right text-[12px] tabular-nums text-todo-muted outline-none"
            />
            <span className="text-[12px] text-todo-muted">
              {task.estimated_minutes ? formatDuration(task.estimated_minutes) : '分钟'}
            </span>
          </label>

          <label className={`${ACT} text-todo-ink2`}>
            <span className={ICO}>
              {task.project ? (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: task.project.color }}
                />
              ) : (
                <span className="text-todo-muted">◇</span>
              )}
            </span>
            <span className="flex-1">项目</span>
            <select
              value={task.project_id ?? ''}
              onChange={(event) =>
                onPatch(task.id, {
                  project_id: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              className="border-0 bg-transparent text-right text-[12px] text-todo-muted outline-none"
            >
              <option value="">未归类</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>

          {/* category（生活面向）和 work_type（精力类型）是既有字段，
              /tracker/plan 一直能筛。/todo 不暴露的话，在这里创建和编辑的任务
              这两个字段会永远是空的，数据慢慢变得半新半旧。 */}
          <label className={`${ACT} text-todo-ink2`}>
            <span className={`${ICO} text-todo-muted`}>#</span>
            <span className="flex-1">生活面向</span>
            <input
              list="todo-categories"
              defaultValue={task.category ?? ''}
              onBlur={(event) => {
                const next = event.target.value.trim() || null;
                if (next !== (task.category ?? null)) onPatch(task.id, { category: next });
              }}
              placeholder="未分类"
              className="w-24 border-0 bg-transparent text-right text-[12px] text-todo-muted outline-none"
            />
            <datalist id="todo-categories">
              {categories.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
          </label>

          <label className={`${ACT} text-todo-ink2`}>
            <span className={`${ICO} text-todo-muted`}>◐</span>
            <span className="flex-1">精力类型</span>
            <select
              value={task.work_type ?? ''}
              onChange={(event) =>
                onPatch(task.id, { work_type: event.target.value === '' ? null : event.target.value })
              }
              className="border-0 bg-transparent text-right text-[12px] text-todo-muted outline-none"
            >
              <option value="">未标</option>
              <option value="deep">深度工作</option>
              <option value="simple">简单</option>
              <option value="shallow">杂事</option>
            </select>
          </label>
        </div>

        <div className={CARD}>
          {running ? (
            <button type="button" className={`${ACT} text-todo-accent`} onClick={onStopTimer}>
              <span className={`${ICO} text-todo-accent`}>■</span>
              <span className="flex-1">停止计时</span>
            </button>
          ) : (
            <button type="button" className={`${ACT} text-todo-ink2`} onClick={() => onStartTimer(task.id)}>
              <span className={`${ICO} text-todo-muted`}>▶</span>
              <span className="flex-1">开始计时</span>
            </button>
          )}

          {/* 原型这里还有一个「🍅 用番茄钟做这条」。本轮没做：
              Home.tsx 的 PomodoroUI 不读 URL 参数，setCurrentTaskId 只从页内的
              handleSelectTask 调用。要让这个按钮真的绑上任务，必须在 PomodoroUI
              里加一个读 task_id 的 useEffect —— 那是番茄钟的代码，本期的硬约束
              是一行不碰（spec 非目标 4）。
              放一个跳过去但不选中任务的按钮比没有按钮更糟，所以先不放。 */}
        </div>

        <div className={CARD} ref={noteCardRef}>
          <div className="px-[13px] pb-[11px] pt-[9px]">
            {/* 标题行照 CommentList / EntryList 的 h4，右栏几张卡片才像同一个人写的。
                「编辑」按钮不只是入口，也是给键盘用户的 —— 下面那块渲染区是个
                div.onClick（不能做成 button：里面的 markdown 可能有链接，
                会变成嵌套可交互元素）。 */}
            <h4 className="mb-1.5 flex items-center text-[11px] font-medium tracking-wide text-todo-muted">
              <span className="flex-1">备注</span>
              {!editingNote && (
                <button
                  type="button"
                  onClick={() => setEditingNote(true)}
                  className="hover:text-todo-accent"
                >
                  编辑
                </button>
              )}
            </h4>

            {editingNote ? (
              <>
                <textarea
                  ref={noteRef}
                  value={note}
                  autoFocus
                  onChange={(event) => setNote(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      setNote(task.description ?? '');
                      setEditingNote(false);
                    }
                  }}
                  onBlur={(event) => {
                    // 点「插入图片」那个 label 也会让 textarea 失焦。此时若退出
                    // 编辑态，上传完成后的回填会落进一个已经不显示的输入框，而且
                    // 不会被保存 —— 图就这么丢了。所以焦点还在这张卡片里就不退出。
                    if (noteCardRef.current?.contains(event.relatedTarget as Node | null)) return;
                    // 上传中同理：占位串还在正文里，这时存下去等于把「![上传中…1]()」
                    // 存成备注。
                    if (noteUploading > 0) return;
                    commitNote();
                  }}
                  placeholder="支持 markdown，可以直接粘图片"
                  className="min-h-[76px] w-full resize-y rounded border border-todo-line bg-todo-card p-2 text-[13px] leading-relaxed text-todo-ink outline-none placeholder:text-todo-muted"
                  {...noteImageHandlers}
                />
                <div className="mt-1 flex items-center gap-2 text-[12px] text-todo-muted">
                  {/* 移动端没有「粘贴截图」这个动作，但能从相册选 */}
                  <label className="cursor-pointer hover:text-todo-accent">
                    插入图片
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(event) => {
                        takeNoteFiles(event.target.files);
                        // 不清空的话同一张图连续选第二次不会再触发 onChange
                        event.target.value = '';
                      }}
                    />
                  </label>
                  {noteUploading > 0 && <span>上传中…</span>}
                  <button
                    type="button"
                    onClick={commitNote}
                    disabled={noteUploading > 0}
                    className="ml-auto rounded-md bg-todo-accent px-2 py-0.5 text-white disabled:opacity-50"
                  >
                    完成
                  </button>
                </div>
              </>
            ) : (
              <div
                onClick={() => setEditingNote(true)}
                className="cursor-text"
                role="presentation"
              >
                {note.trim() ? (
                  <Markdown className="text-[13px] text-todo-ink">{note}</Markdown>
                ) : (
                  <span className="text-[13px] text-todo-muted">添加备注</span>
                )}
              </div>
            )}

            {noteError && <div className="mt-1.5 text-[12px] text-todo-urgent">{noteError}</div>}
          </div>
        </div>

        {/* 「哪几次会话干过它」排在留言之前：它和备注一样是关于「这件事是什么」
            的事实，而留言是过程。只读 —— 关联由 AI 写日志时带上，或走
            /worklogs/:slug/task_links 那个端点，这一栏不提供增删。 */}
        <LinkedWorklogs worklogs={task.linked_worklogs} />
        <LinkedWikis wikis={task.linked_wikis} />

        {/* 备注是「这件事是什么」，留言是「这件事的过程」，时间记录是「花了
            多久」—— 从定义到过程到计量，从上往下读得通（spec §2.1）。
            增删留言会变中栏徽标的数字，所以要 onRefresh。 */}
        <CommentList taskId={task.id} onChanged={onRefresh} />

        {/* 补录和删除会改总时长，左栏的「今天已专注」也跟着变，所以要重取 */}
        <EntryList taskId={task.id} onChanged={onRefresh} />
      </div>

      <div className="flex items-center justify-between border-t border-todo-line px-[14px] py-[9px] text-[11.5px] text-todo-muted">
        <span>
          创建于{' '}
          {new Date(task.created_at).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })}
        </span>
        {/* 内联二次确认而不是弹窗：不引组件库，也不打断视线。
            删除会连带删掉这条任务的全部步骤（Task 的 dependent: :destroy），
            计时记录会保留但解绑（dependent: :nullify）——所以值得问一句。 */}
        {confirmingDelete ? (
          <span className="flex items-center gap-2">
            <span className="text-todo-ink2">
              删除这条{task.subtasks.length > 0 && ` 和 ${task.subtasks.length} 个步骤`}？
            </span>
            <button
              type="button"
              onClick={() => onDestroy(task.id)}
              className="rounded-md px-2 py-0.5 text-todo-urgent hover:bg-todo-card2"
            >
              删除
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="rounded-md px-2 py-0.5 hover:bg-todo-card2"
            >
              取消
            </button>
          </span>
        ) : (
          <button
            type="button"
            title="删除"
            aria-label="删除任务"
            onClick={() => setConfirmingDelete(true)}
            className="rounded-md px-[5px] py-0.5 text-[14px] text-todo-muted hover:bg-todo-card2 hover:text-todo-urgent"
          >
            🗑
          </button>
        )}
      </div>
    </aside>
  );
};

export default DetailPane;
